import { Checkbox } from "@base-ui/react/checkbox";
import { useTransferStore, type Slot } from "../store/transferStore";

interface ClipListProps {
  slot: Slot;
  title: string;
}

/** Check mark that flips to a dash when the checkbox is indeterminate. */
function CheckGlyph() {
  return (
    <Checkbox.Indicator className="cb-ind">
      <svg viewBox="0 0 10 10" aria-hidden>
        <path className="check" d="M2 5.2 4.2 7.4 8 2.8" />
        <path className="dash" d="M2.4 5h5.2" />
      </svg>
    </Checkbox.Indicator>
  );
}

/**
 * Checkbox list of one model's animation clips. Source clips transfer when
 * checked; target clips are the model's existing animations and are kept when
 * checked (unchecking removes them from the exported file).
 */
export function ClipList({ slot, title }: ClipListProps) {
  const slotState = useTransferStore((s) => s[slot]);
  const toggleClip = useTransferStore((s) => s.toggleClip);
  const setAllClips = useTransferStore((s) => s.setAllClips);
  const busy = useTransferStore((s) => s.busy);

  if (!slotState.gltf) return null;

  const { clips, selected } = slotState;

  if (clips.length === 0) {
    return (
      <div className="clips">
        <div className="clips-head">
          <span className="clips-title">{title}</span>
        </div>
        <div className="clips-empty">no animation clips</div>
      </div>
    );
  }

  const checkedCount = selected.filter(Boolean).length;
  const allChecked = checkedCount === clips.length;
  const partial = checkedCount > 0 && !allChecked;

  return (
    <div className="clips">
      <div className="clips-head">
        <span className="clips-title">{title}</span>
        <label className="clips-all">
          <Checkbox.Root
            className="cb"
            checked={allChecked}
            indeterminate={partial}
            disabled={busy}
            onCheckedChange={(checked) => setAllClips(slot, checked)}
          >
            <CheckGlyph />
          </Checkbox.Root>
          all
        </label>
      </div>
      <ul className="clip-rows">
        {clips.map((name, i) => (
          <li key={i}>
            <label className="clip-row">
              <Checkbox.Root
                className="cb"
                checked={selected[i] ?? false}
                disabled={busy}
                onCheckedChange={() => toggleClip(slot, i)}
              >
                <CheckGlyph />
              </Checkbox.Root>
              <span className="clip-name">{name}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
